/* TAMAN form pages: Za tvrtke (B2B), Raskid ugovora and Kontakt. All three post Shopify's
   contact form; this file (shared by the three sections, named after the one that needs
   it most) adds what the browser does not: Croatian inline errors, an error summary that
   takes focus, the OIB check (ISO 7064 MOD 11,10), fields that only appear when they apply,
   the B2B inquiry type, focus on the success message, the real submission time on the
   withdrawal receipt and the model withdrawal form printing open. Without it, native
   validation (required, type, pattern) still guards every form. Messages come from
   data-msg-* attributes rendered from the locale. */
(() => {
  const SENT = 'taman:form-sent:';
  const store = {
    get(k) { try { return window.sessionStorage.getItem(k); } catch (e) { return null; } },
    set(k, v) { try { window.sessionStorage.setItem(k, v); } catch (e) { /* storage blocked */ } },
    del(k) { try { window.sessionStorage.removeItem(k); } catch (e) { /* storage blocked */ } }
  };

  /* OIB: 11 digits, the last one a MOD 11,10 check digit */
  const isOib = (v) => {
    if (!/^\d{11}$/.test(v)) return false;
    let a = 10;
    for (let i = 0; i < 10; i += 1) {
      a = (a + Number(v[i])) % 10 || 10;
      a = (a * 2) % 11;
    }
    return (11 - a) % 10 === Number(v[10]);
  };
  const cleanOib = (v) => v.replace(/^\s*hr/i, '').replace(/[\s.\-/]/g, '');

  /* the strftime subset used by the locale's date format (%-d. %-m. %Y. u %H:%M) */
  const strftime = (d, fmt) => {
    const parts = { d: d.getDate(), m: d.getMonth() + 1, Y: d.getFullYear(), H: d.getHours(), M: d.getMinutes() };
    return fmt.replace(/%(-?)([dmYHM])/g, (_, bare, k) => (bare || k === 'Y' ? String(parts[k]) : String(parts[k]).padStart(2, '0')));
  };
  const today = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };
  const focusSoon = (el) => requestAnimationFrame(() => el.focus());

  class PageForm {
    constructor(form) {
      this.form = form;
      this.summary = form.querySelector('[data-form-summary]');
      this.list = form.querySelector('[data-form-summary-list]');
      this.button = form.querySelector('[type="submit"]');
      this.inquiry = form.querySelector('[data-inquiry-type]');
      form.noValidate = true;
      form.querySelectorAll('[data-max-today]').forEach((el) => { el.max = today(); });
      /* capture: validate before Shopify's captcha handler can take over the submit */
      form.addEventListener('submit', (e) => this.onSubmit(e), true);
      form.addEventListener('change', (e) => this.onEdit(e, true));
      form.addEventListener('input', (e) => this.onEdit(e, false));
      form.addEventListener('focusout', (e) => {
        const f = e.target;
        if (f.dataset && f.dataset.validate === 'oib' && f.value) f.value = cleanOib(f.value);
      });
      if (this.summary) this.summary.addEventListener('click', (e) => this.onSummaryClick(e));
      window.addEventListener('pageshow', () => { this.sync(); this.busy(false); });
      this.sync();
      if (this.summary && !this.summary.hidden) focusSoon(this.summary);
    }

    /* conditional fields: shown, enabled and (if marked) required only while they apply */
    sync() {
      const on = new Set();
      this.form.querySelectorAll('[data-reveals]').forEach((el) => {
        if (el.tagName === 'OPTION' ? el.selected : el.checked) on.add(el.dataset.reveals);
      });
      this.form.querySelectorAll('[data-reveal-when]').forEach((box) => {
        const shown = on.has(box.dataset.revealWhen);
        box.classList.toggle('is-shown', shown);
        box.querySelectorAll('input, select, textarea').forEach((f) => {
          f.disabled = !shown;
          if (f.hasAttribute('data-required-when-shown')) f.required = shown;
          if (!shown) this.clear(f);
        });
      });
      if (this.inquiry) {
        const pallet = this.form.querySelector('[data-reveals="pallet"]');
        this.inquiry.value = pallet && pallet.checked ? this.inquiry.dataset.palletValue : this.inquiry.dataset.defaultValue;
      }
    }

    fields() {
      return [...this.form.elements].filter((f) => f.name && !f.disabled && !['hidden', 'submit', 'button'].includes(f.type));
    }

    /* the message for a field, or '' when it is fine */
    check(f) {
      const d = f.dataset;
      if (f.type === 'checkbox') return f.required && !f.checked ? d.msgRequired || '' : '';
      const value = (f.value || '').trim();
      if (!value) return f.required ? d.msgRequired || '' : '';
      if (d.validate === 'oib') {
        const oib = cleanOib(value);
        if (!/^\d{11}$/.test(oib)) return d.msgLength || '';
        return isOib(oib) ? '' : d.msgChecksum || '';
      }
      if (d.validate === 'postal-city') return /\d{5}/.test(value) && /\p{L}{2}/u.test(value) ? '' : d.msgFormat || '';
      if (d.validate === 'phone') return value.replace(/\D/g, '').length >= 6 ? '' : d.msgFormat || '';
      if (f.type === 'email') return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value) ? '' : d.msgFormat || '';
      const v = f.validity;
      return v && (v.badInput || v.patternMismatch || v.rangeOverflow || v.rangeUnderflow || v.typeMismatch) ? d.msgFormat || d.msgRequired || '' : '';
    }

    errorBox(f) { return f.id ? document.getElementById(`${f.id}-error`) : null; }

    describe(f, id, add) {
      const rest = (f.getAttribute('aria-describedby') || '').split(/\s+/).filter((x) => x && x !== id);
      const ids = add ? [id, ...rest] : rest;
      if (ids.length) f.setAttribute('aria-describedby', ids.join(' '));
      else f.removeAttribute('aria-describedby');
    }

    show(f, msg) {
      f.setAttribute('aria-invalid', 'true');
      const box = this.errorBox(f);
      if (!box) return;
      const text = box.querySelector('[data-field-error-text]');
      if (text) text.textContent = msg;
      box.hidden = false;
      this.describe(f, box.id, true);
    }

    clear(f) {
      f.removeAttribute('aria-invalid');
      const box = this.errorBox(f);
      if (!box) return;
      box.hidden = true;
      this.describe(f, box.id, false);
    }

    onEdit(e, changed) {
      const f = e.target;
      if (changed && (f.matches('[data-reveals]') || (f.tagName === 'SELECT' && f.querySelector('[data-reveals]')))) this.sync();
      /* errors appear on submit; once shown, they clear as soon as the field is right */
      if (f.getAttribute && f.getAttribute('aria-invalid') === 'true' && !this.check(f)) this.clear(f);
    }

    onSubmit(e) {
      this.sync();
      const invalid = [];
      this.fields().forEach((f) => {
        if (f.dataset.validate === 'oib') f.value = cleanOib(f.value);
        const msg = this.check(f);
        if (msg) { this.show(f, msg); invalid.push([f, msg]); } else this.clear(f);
      });
      if (invalid.length) {
        e.preventDefault();
        e.stopImmediatePropagation();
        this.report(invalid);
        return;
      }
      if (this.summary) this.summary.hidden = true;
      this.busy(true);
      store.set(SENT + this.form.id, String(Date.now()));
    }

    report(invalid) {
      if (!this.summary || !this.list) { invalid[0][0].focus(); return; }
      this.list.replaceChildren(...invalid.map(([f, msg]) => {
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.href = `#${f.id}`;
        a.textContent = msg;
        li.append(a);
        return li;
      }));
      this.summary.hidden = false;
      this.summary.focus();
    }

    onSummaryClick(e) {
      const link = e.target.closest('a[href^="#"]');
      if (!link) return;
      const f = document.getElementById(decodeURIComponent(link.hash.slice(1)));
      if (!f) return;
      e.preventDefault();
      const label = this.form.querySelector(`label[for="${CSS.escape(f.id)}"]`);
      (label || f).scrollIntoView({ block: 'center' });
      f.focus({ preventScroll: true });
    }

    busy(on) {
      if (!this.button) return;
      clearTimeout(this.timer);
      this.button.classList.toggle('is-loading', on);
      if (on) {
        this.button.setAttribute('aria-busy', 'true');
        /* Shopify may stop the post for a challenge; never leave the button spinning */
        this.timer = setTimeout(() => this.busy(false), 12000);
      } else this.button.removeAttribute('aria-busy');
    }
  }

  /* after a successful post: real submission time on the receipt, focus on the message */
  const settle = (form, success) => {
    const sent = Number(store.get(SENT + form.id));
    store.del(SENT + form.id);
    if (sent && Date.now() - sent < 30 * 60 * 1000) {
      const when = new Date(sent);
      success.querySelectorAll('[data-sent-at]').forEach((el) => {
        el.textContent = strftime(when, el.dataset.format || '%-d. %-m. %Y. %H:%M');
        el.setAttribute('datetime', when.toISOString());
      });
    }
    focusSoon(success);
  };

  const init = (root = document) => {
    root.querySelectorAll('form[data-page-form]').forEach((form) => {
      if (form.dataset.ready) return;
      form.dataset.ready = '1';
      const success = form.querySelector('[data-form-success]');
      if (success) settle(form, success);
      else new PageForm(form);
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));

  /* the model withdrawal form prints open, and closes again afterwards if it was closed */
  if (!window.TamanPrintOpen) {
    window.TamanPrintOpen = true;
    window.addEventListener('beforeprint', () => {
      document.querySelectorAll('details[data-print-open]:not([open])').forEach((d) => {
        d.open = true;
        d.dataset.printOpened = '1';
      });
    });
    window.addEventListener('afterprint', () => {
      document.querySelectorAll('details[data-print-opened]').forEach((d) => {
        d.open = false;
        delete d.dataset.printOpened;
      });
    });
  }
})();
